import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import React, { ChangeEvent } from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart, PatientDoc, Descriptor, MedicareDescriptors, FileTypes, PhotoDimensions } from '@usacsmideo/types';
import useChart from '../../../../hooks/useChart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { FormInput } from '../../../../components/forms/FormInput';
import { Button } from '../../../../components/buttons/Button';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { useInitChartSection } from '../../../../hooks/useInitChartSection';
import { notifyChartSaveError } from '../../../../utilities/chartSaveError';
import { PhotoCamera2 } from '../../../../components/PhotoCamera2';
import { RetakePhoto, TakePhoto } from '../../../../components/buttons/';
import { Uploader } from '../../../../components/Uploader';
import { UploadSquareSolid } from 'iconoir-react';
import { isMobile } from 'react-device-detect';
import { Heading } from '../../../../components/Heading';
import * as Sentry from '@sentry/browser';
import Checkbox from '../../../../components/forms/Checkbox';
import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
export const Route = createFileRoute('/_authenticated/$patientId/payment/medicareInfo')({
  component: MedicareInfoPage,
});

function MedicareInfoPage() {
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const { mutate, mutateAsync } = useUpdateChartSection();

  const [medicareDescriptors, setMedicareDescriptors] = React.useState<MedicareDescriptors>([]);
  const [traditionalMedicare, setTraditionalMedicare] = React.useState(false);
  const [medicareAdvantage, setMedicareAdvantage] = React.useState(false);
  const [medicareSupplement, setMedicareSupplement] = React.useState(false);
  const [other, setOther] = React.useState(false);
  const [prefersUpload, setPrefersUpload] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  // const [navDisabled, setNavDisabled] = React.useState(true);
  const [takingPhoto, setTakingPhoto] = React.useState({
    medicareFront: false,
    medicareBack: false,
    medicareFront2: false,
    medicareBack2: false,
  });
  const [warned, setWarned] = React.useState(false);
  const [photoOneWorking, setPhotoOneWorking] = React.useState(false);
  const [photoTwoWorking, setPhotoTwoWorking] = React.useState(false);
  const [photoThreeWorking, setPhotoThreeWorking] = React.useState(false);
  const [photoFourWorking, setPhotoFourWorking] = React.useState(false);
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  // WARN MOBILE USERS THAT ENABLING CAMERA DISABLES AUDIO ON CONCIERGE CALL
  React.useEffect(() => {
    if (patientInfo && patientInfo.navType?.base === 'concierge' && isMobile && !warned) {
      alert(
        'If you can no longer hear your concierge after using your camera, you may need to return to your Telehealth call and refresh your browser to reconnect with your clinician/concierge',
      );
      setWarned(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warned]);

  // SET STATE TO TRIGGER EFFECT
  const handleCheckboxClick = (e: ChangeEvent<HTMLInputElement>) => {
    switch (e.target.name) {
      case 'traditionalMedicare':
        setTraditionalMedicare(!traditionalMedicare);
        setMedicareAdvantage(false);
        break;
      case 'medicareAdvantage':
        setMedicareAdvantage(!medicareAdvantage);
        setTraditionalMedicare(false);
        break;
      case 'medicareSupplement':
        setMedicareSupplement(!medicareSupplement);
        break;
      case 'other':
        setOther(!other);
        break;
      default:
        break;
    }
  };

  const handleRetakePhoto = (pic: string) => {
    mutate(
      {
        patientId: patientId,
        docData: {
          MEDICARE_INFO: {
            recipient: true,
            medicareDescriptors: medicareDescriptors,
            planName: medicareForm.state.values.planName,
            medicareCardFrontURI:
              chart.MEDICARE_INFO.medicareCardFrontURI && pic !== 'medicareCardFrontURI'
                ? chart.MEDICARE_INFO.medicareCardFrontURI
                : '',
            medicareCardBackURI:
              chart.MEDICARE_INFO.medicareCardBackURI && pic !== 'medicareCardBackURI'
                ? chart.MEDICARE_INFO.medicareCardBackURI
                : '',
            medicareCardFrontURI2:
              chart.MEDICARE_INFO.medicareCardFrontURI2 && pic !== 'medicareCardFrontURI2'
                ? chart.MEDICARE_INFO.medicareCardFrontURI2
                : '',
            medicareCardBackURI2:
              chart.MEDICARE_INFO.medicareCardBackURI2 && pic !== 'medicareCardBackURI2'
                ? chart.MEDICARE_INFO.medicareCardBackURI2
                : '',
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // UPDATE MEDICARE DESCRIPTORS ON STATE CHANGE
  React.useEffect(() => {
    const cloned: MedicareDescriptors = [...medicareDescriptors];
    function removeItem(value: Descriptor) {
      const index = cloned.indexOf(value);
      if (index > -1) {
        cloned.splice(index, 1);
      }
      setMedicareDescriptors(cloned);
      return;
    }
    if (traditionalMedicare) {
      if (cloned.includes('Traditional Medicare') === false) {
        cloned.push('Traditional Medicare');
      }
      setMedicareDescriptors(cloned);
    } else {
      removeItem('Traditional Medicare');
    }

    if (medicareAdvantage) {
      if (cloned.includes('Medicare Advantage') === false) {
        cloned.push('Medicare Advantage');
      }
      setMedicareDescriptors(cloned);
    } else {
      removeItem('Medicare Advantage');
    }

    if (medicareSupplement) {
      if (cloned.includes('I Have A Medicare Supplement') === false) {
        cloned.push('I Have A Medicare Supplement');
      }
      setMedicareDescriptors(cloned);
    } else {
      removeItem('I Have A Medicare Supplement');
    }

    if (other) {
      if (cloned.includes('Other') === false) {
        cloned.push('Other');
      }
      setMedicareDescriptors(cloned);
    } else {
      removeItem('Other');
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traditionalMedicare, medicareAdvantage, medicareSupplement, other]);

  // THE FORM DATA TO WRITE — SHARED BY THE SUBMIT AND THE BACKGROUND INIT, SO BOTH WRITE THE SAME
  const medicareInfoPayload = () => ({
    patientId: patientId,
    docData: {
      MEDICARE_INFO: {
        recipient: true,
        medicareDescriptors: medicareDescriptors,
        planName: medicareForm.state.values.planName,
        medicareCardFrontURI: chart?.MEDICARE_INFO?.medicareCardFrontURI ? chart.MEDICARE_INFO.medicareCardFrontURI : '',
        medicareCardBackURI: chart?.MEDICARE_INFO?.medicareCardBackURI ? chart.MEDICARE_INFO.medicareCardBackURI : '',
        medicareCardFrontURI2: chart?.MEDICARE_INFO?.medicareCardFrontURI2
          ? chart.MEDICARE_INFO.medicareCardFrontURI2
          : '',
        medicareCardBackURI2: chart?.MEDICARE_INFO?.medicareCardBackURI2 ? chart.MEDICARE_INFO.medicareCardBackURI2 : '',
      },
    },
  });

  // WRITE FORM DATA TO FIRESTORE
  const handleMedicareInfoUpdate = () => {
    mutate(medicareInfoPayload(), {
      onSuccess: () => {
        // alert('Successfully updated document');
        setIsSubmitted(true);
      },
      onError: () => {
        notifyChartSaveError();
      },
    });
  };

  // INITIALIZE EMPTY FIRESTORE DOC IF IT DOESN'T EXIST — ONCE PER FAILURE, NOT ONCE PER SNAPSHOT
  useInitChartSection({
    needed: !!chart && !chart.MEDICARE_INFO,
    write: () => mutateAsync(medicareInfoPayload()).then(() => setIsSubmitted(true)),
  });

  // INITIALIZE FORM - MOST DATA IS HANDLED OUTSIDE OF THE FORM
  const medicareForm = useForm({
    defaultValues: {
      recipient: true,
      planName: chart?.MEDICARE_INFO?.planName ? chart.MEDICARE_INFO.planName : '',
    },
    onSubmit: async () => {
      await handleMedicareInfoUpdate();
    },
  });
  // SET INITIAL DESCRIPTOR VALUES
  React.useEffect(() => {
    if (chart?.MEDICARE_INFO?.medicareDescriptors) {
      const descriptors = chart.MEDICARE_INFO.medicareDescriptors;
      if (descriptors.includes('Traditional Medicare')) {
        setTraditionalMedicare(true);
      }
      if (descriptors.includes('Medicare Advantage')) {
        setMedicareAdvantage(true);
      }
      if (descriptors.includes('I Have A Medicare Supplement')) {
        setMedicareSupplement(true);
      }
      if (descriptors.includes('Other')) {
        setOther(true);
      }
    }
  }, [chart?.MEDICARE_INFO?.medicareDescriptors]);

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.PATIENT_INFO_1) {
    return (
      <>
        <div id="medicare-page" className="flex w-screen flex-col items-center justify-center">
          <Heading id="medicare-page-header" textContent="Medicare Information" />
          <div id="medicare-page-main" className="flex w-5/6 flex-row justify-around">
            <div className="flex flex-col items-start">
              <div id="medicare-checkbox-group" className="flex flex-col">
                <Checkbox
                  id="medicare-traditional"
                  label="Traditional Medicare"
                  name="traditionalMedicare"
                  checked={traditionalMedicare}
                  onChange={(e) => {
                    handleCheckboxClick(e);
                  }}
                />
                <Checkbox
                  id="medicare-advantage"
                  label="Medicare Advantage"
                  name="medicareAdvantage"
                  checked={medicareAdvantage}
                  onChange={(e) => {
                    handleCheckboxClick(e);
                  }}
                />
                <Checkbox
                  id="medicare-supplement"
                  label="I Have A Medicare Supplement"
                  name="medicareSupplement"
                  checked={medicareSupplement}
                  onChange={(e) => {
                    handleCheckboxClick(e);
                  }}
                />
                <Checkbox
                  id="medicare-other"
                  label="Other"
                  name="other"
                  checked={other}
                  onChange={(e) => {
                    handleCheckboxClick(e);
                  }}
                />
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  medicareForm.handleSubmit();
                }}
              >
                <div className="flex flex-row">
                  <medicareForm.Field
                    name="planName"
                    children={(field) => (
                      <>
                        <FormInput
                          label="Name(s) of Plan(s): "
                          id="medicare-plans"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                      </>
                    )}
                  />
                </div>
              </form>
              {/* FRONT OF MAIN MEDICARE CARD */}
              <div id="medicare-front-container" className="my-4">
                <p className="text-2xl font-bold">{`Front of Medicare ${medicareAdvantage ? 'Advantage ' : ' '}Card:`}</p>
                {chart?.MEDICARE_INFO?.medicareCardFrontURI && takingPhoto.medicareFront === false ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <img
                      src={chart.MEDICARE_INFO?.medicareCardFrontURI}
                      style={{ width: `${PhotoDimensions.desktopWidth}%` }}
                      alt="Medicare Card Front"
                    />
                    <RetakePhoto setter={() => handleRetakePhoto('medicareCardFrontURI')} />
                  </div>
                ) : (
                  takingPhoto.medicareFront === false && (
                    <>
                      <div className="flex flex-row">
                        <TakePhoto setter={() => setTakingPhoto({ ...takingPhoto, medicareFront: true })} />
                        <button
                          onClick={() => {
                            setPrefersUpload(!prefersUpload);
                          }}
                        >
                          <UploadSquareSolid />
                        </button>
                      </div>
                      {prefersUpload && (
                        <Uploader
                          {...{
                            docToUpdate: 'MEDICARE_INFO',
                            bucket: 'patient',
                            type: FileTypes.medicareFront,
                            title: 'Upload Front of Medicare Card',
                            elementId: 'medicare-card-front-file-upload',
                          }}
                        />
                      )}
                    </>
                  )
                )}
                {takingPhoto.medicareFront && (
                  <PhotoCamera2
                    type={FileTypes.medicareFront}
                    docToUpdate="MEDICARE_INFO"
                    style={'card'}
                    bucket={'patient'}
                    path={`user/${patientId}/images/medicareCardFront`}
                    patientId={patientId}
                    hideCamera={() => {
                      setPhotoOneWorking(false);
                      setTakingPhoto({ ...takingPhoto, medicareFront: false });
                    }}
                    working={photoOneWorking}
                    setWorking={setPhotoOneWorking}
                  />
                )}
              </div>
              {/* BACK OF MAIN MEDICARE CARD */}
              <div id="medicare-back-container" className="my-4">
                <p className="text-2xl font-bold">{`Back of Medicare ${medicareAdvantage ? 'Advantage ' : ' '}Card:`}</p>
                {chart?.MEDICARE_INFO?.medicareCardBackURI && takingPhoto.medicareBack === false ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <img
                      src={chart.MEDICARE_INFO?.medicareCardBackURI}
                      style={{ width: `${PhotoDimensions.desktopWidth}%` }}
                      alt="Medicare Card Back"
                    />
                    <RetakePhoto setter={() => handleRetakePhoto('medicareCardBackURI')} />
                  </div>
                ) : (
                  takingPhoto.medicareBack === false && (
                    <>
                      <div className="flex flex-row">
                        <TakePhoto setter={() => setTakingPhoto({ ...takingPhoto, medicareBack: true })} />
                        <button
                          onClick={() => {
                            setPrefersUpload(!prefersUpload);
                          }}
                        >
                          <UploadSquareSolid />
                        </button>
                      </div>
                      {prefersUpload && (
                        <Uploader
                          {...{
                            docToUpdate: 'MEDICARE_INFO',
                            bucket: 'patient',
                            type: FileTypes.medicareBack,
                            title: 'Upload Back of Medicare Card',
                            elementId: 'medicare-card-back-file-upload',
                          }}
                        />
                      )}
                    </>
                  )
                )}
                {takingPhoto.medicareBack && (
                  <PhotoCamera2
                    type={FileTypes.medicareBack}
                    docToUpdate="MEDICARE_INFO"
                    style={'card'}
                    bucket={'patient'}
                    path={`user/${patientId}/images/medicareCardBack`}
                    patientId={patientId}
                    hideCamera={() => {
                      setPhotoTwoWorking(false);
                      setTakingPhoto({ ...takingPhoto, medicareBack: false });
                    }}
                    working={photoTwoWorking}
                    setWorking={setPhotoTwoWorking}
                  />
                )}
              </div>
              {/* SUPPLEMENTAL CARD - CONDITIONALLY RENDERED */}
              {other || medicareSupplement ? (
                <div id="supplement-front-container">
                  {/* FRONT OF SUPPLEMENTAL MEDICARE CARD */}
                  <div className="my-4">
                    <p className="text-2xl font-bold">Front of Additional Card:</p>
                    {chart?.MEDICARE_INFO?.medicareCardFrontURI2 && takingPhoto.medicareFront2 === false ? (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}
                      >
                        <img
                          src={chart.MEDICARE_INFO?.medicareCardFrontURI2}
                          style={{ width: `${PhotoDimensions.desktopWidth}%` }}
                          alt="Other Card Front"
                        />
                        <RetakePhoto setter={() => handleRetakePhoto('medicareCardFrontURI2')} />
                      </div>
                    ) : (
                      takingPhoto.medicareFront2 === false && (
                        <>
                          <div className="flex flex-row">
                            <TakePhoto setter={() => setTakingPhoto({ ...takingPhoto, medicareFront2: true })} />
                            <button
                              onClick={() => {
                                setPrefersUpload(!prefersUpload);
                              }}
                            >
                              <UploadSquareSolid />
                            </button>
                          </div>
                          {prefersUpload && (
                            <Uploader
                              {...{
                                docToUpdate: 'MEDICARE_INFO',
                                bucket: 'patient',
                                type: FileTypes.medicareFront2,
                                title: 'Upload Front of Other Card',
                                elementId: 'other-card-front-file-upload',
                              }}
                            />
                          )}
                        </>
                      )
                    )}
                    {takingPhoto.medicareFront2 && (
                      <PhotoCamera2
                        type={FileTypes.medicareFront2}
                        docToUpdate="MEDICARE_INFO"
                        style={'card'}
                        bucket={'patient'}
                        path={`user/${patientId}/images/medicareCardFront2`}
                        patientId={patientId}
                        hideCamera={() => {
                          setPhotoThreeWorking(false);
                          setTakingPhoto({ ...takingPhoto, medicareFront2: false });
                        }}
                        working={photoThreeWorking}
                        setWorking={setPhotoThreeWorking}
                      />
                    )}
                  </div>
                  {/* BACK OF SUPPLEMENTAL MEDICARE CARD */}
                  <div id="supplement-back-container" className="my-4">
                    <p className="text-2xl font-bold">Back of Additional Card:</p>
                    {chart?.MEDICARE_INFO?.medicareCardBackURI2 && takingPhoto.medicareBack2 === false ? (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}
                      >
                        <img
                          src={chart.MEDICARE_INFO?.medicareCardBackURI2}
                          style={{ width: `${PhotoDimensions.desktopWidth}%` }}
                          alt="Other Card Front"
                        />
                        <RetakePhoto setter={() => handleRetakePhoto('medicareCardBackURI2')} />
                      </div>
                    ) : (
                      takingPhoto.medicareBack2 === false && (
                        <>
                          <div className="flex flex-row">
                            <TakePhoto setter={() => setTakingPhoto({ ...takingPhoto, medicareBack2: true })} />
                            <button
                              onClick={() => {
                                setPrefersUpload(!prefersUpload);
                              }}
                            >
                              <UploadSquareSolid />
                            </button>
                          </div>
                          {prefersUpload && (
                            <Uploader
                              {...{
                                docToUpdate: 'MEDICARE_INFO',
                                bucket: 'patient',
                                type: FileTypes.medicareBack2,
                                title: 'Upload Front of Other Card',
                                elementId: 'other-card-back-file-upload',
                              }}
                            />
                          )}
                        </>
                      )
                    )}
                    {takingPhoto.medicareBack2 && (
                      <PhotoCamera2
                        type={FileTypes.medicareBack2}
                        docToUpdate="MEDICARE_INFO"
                        style={'card'}
                        bucket={'patient'}
                        path={`user/${patientId}/images/medicareCardBack2`}
                        patientId={patientId}
                        hideCamera={() => {
                          setPhotoFourWorking(false);
                          setTakingPhoto({ ...takingPhoto, medicareBack2: false });
                        }}
                        working={photoFourWorking}
                        setWorking={setPhotoFourWorking}
                      />
                    )}
                  </div>
                </div>
              ) : null}
              <div className="flex w-full flex-col items-center">
                <Button id="submit-medicare" type="submit" textContent="Submit" onClick={handleMedicareInfoUpdate} />
              </div>
            </div>
          </div>
          <div id="nav" className="flex flex-col items-center">
            {/* {prevPage && <NavBack to={prevPage} />} */}
            {isSubmitted && (
              <div className="flex flex-col items-center">
                <button
                  id="submit-form-info"
                  title="Submit Form Info"
                  onClick={handleNextButtonClick}
                  disabled={false}
                  className={classNames.nextButton}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
